import { Controller } from "@hotwired/stimulus"

// Shows a spinner and disables the submit button while the form submits.
// Usage: data-controller="submit-button" on a wrapper, with targets on button and spinner.
//
// The form is also found through the button's own form= association, so a
// button in a modal footer (outside the <form>) works. `resetAfter` (ms)
// re-enables the button for submits whose response stays on the page — a file
// download never navigates away, so without it the button would stay disabled.
// A page restored from the back-forward cache is always reset, or Back would
// show a dead button. A Turbo submission the server refused (422 with a stream
// that keeps the form on screen, e.g. a validation message in a modal) resets
// at once, so the user can correct the input and send it again.
export default class extends Controller {
  static targets = ["button", "spinner"]
  static values = { resetAfter: Number }

  connect() {
    this.form = this.element.closest("form") || (this.hasButtonTarget ? this.buttonTarget.form : null)
    this.handleSubmit = () => this.disable()
    this.handlePageShow = (event) => {
      if (event.persisted) this.reset()
    }
    this.handleSubmitEnd = (event) => {
      if (event.detail?.success === false) this.reset()
    }
    this.form?.addEventListener("submit", this.handleSubmit)
    this.form?.addEventListener("turbo:submit-end", this.handleSubmitEnd)
    window.addEventListener("pageshow", this.handlePageShow)
  }

  disconnect() {
    this.form?.removeEventListener("submit", this.handleSubmit)
    this.form?.removeEventListener("turbo:submit-end", this.handleSubmitEnd)
    window.removeEventListener("pageshow", this.handlePageShow)
    window.clearTimeout(this.resetTimer)
  }

  disable() {
    if (this.hasButtonTarget) {
      this.buttonTarget.disabled = true
      this.buttonTarget.setAttribute("aria-busy", "true")
    }
    if (this.hasSpinnerTarget) this.spinnerTarget.classList.remove("d-none")
    if (this.resetAfterValue > 0) this.resetTimer = window.setTimeout(() => this.reset(), this.resetAfterValue)
  }

  reset() {
    window.clearTimeout(this.resetTimer)
    if (this.hasButtonTarget) {
      this.buttonTarget.disabled = false
      this.buttonTarget.removeAttribute("aria-busy")
    }
    if (this.hasSpinnerTarget) this.spinnerTarget.classList.add("d-none")
  }
}
